import { CLOSING_MSG, RECURRING_MSG, DELIVERY_NOTE_MSG, HOME_MSG, PURCHASE_MSG, NEW_COMPANY_MSG, RFQ_MSG, SWITCHER_MSG, type HomeSummary, type Me, type MonthFigures, MAIL_LIMITS, MAIL_MSG } from "@genbaryoku/contract";
import { PARTNER_LINK_MSG } from "./schedule/msg.js";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { api, messageOf } from "../api.js";
import { usePageName } from "../components/BackButton.jsx";
import { Icon } from "../components/Shell.jsx";
import { ErrorText, Help, useToast } from "../components/ui.jsx";
import { uses } from "../core/uses.js";
import { t, useLabels } from "../labels.js";
import { daysSince, md, yen } from "../lib/num.js";
import { Slot } from "../core/slots.jsx";
import { useReportCountState } from "./schedule/Reports.jsx";
import { AnnouncementBanner, useAnnouncements } from "../components/Support.jsx";
import { notify } from "../components/Notice.jsx";
import { HomePurchasesDue } from "./purchases/HomePurchasesDue.jsx";

const YOUBI = ["日", "月", "火", "水", "木", "金", "土"];
export function fmtToday(d = new Date()): string {
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日（${YOUBI[d.getDay()]}）`;
}

/**
 * ホーム「本日の業務」（docs/10 §9.2、試作 v3.9.1 → 9/29 home.html → 10/21 Figma「ホームの圧縮（10/21）」①改＝#226）。数字は /api/v1/home（docs/10 §9.2 の対応表）。
 * 見積が1枚も無いうちは「まず1枚」。1枚できるとカードに数字が入る。
 * 9/29（#91）：画面に置く文は「その画面でしか分からない事実」だけ（docs/10 §5.1.1）。使い方は見出しの「？」へ
 * 10/21（#226）：カードは 1行に4枚・高さは内容なり（約 122px）。題の行（丸い印 28px＋題）→ 数の行（数字 32px＋「件」・右端に「表示 →」36px）→ 副題 1行。
 *   全幅の藍のボタンはやめた。文は contract の HOME_MSG（言葉の切替は t()）
 */
export function HomePage({ me }: { me: Me }) {
  useLabels(); // t() が会社の言葉で埋める（言葉が変われば描き直す）
  usePageName(HOME_MSG.menu);
  const loc = useLocation();
  const nav = useNavigate();
  const justRegistered = !!(loc.state as { justRegistered?: boolean } | null)?.justRegistered;
  const reopened = !!(loc.state as { reopened?: boolean } | null)?.reopened;
  const switched = (loc.state as { switched?: string } | null)?.switched;
  // 新しい会社を作った直後（#99、docs/38）：トースト「「○○」を作りました」＋「自社情報を入れる」。再読み込みでまた出さないよう state は消す
  const created = (loc.state as { created?: string } | null)?.created;
  const { toast, node: toastNode } = useToast();
  useEffect(() => {
    if (created === undefined) return;
    toast(NEW_COMPANY_MSG.created(created), { label: NEW_COMPANY_MSG.toCompany, onClick: () => nav("/settings/company") });
    nav(loc.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created]);
  // 会社を切り替えた直後（#151、docs/60 §2）：トースト「{会社名} に切り替えました」（同じ製品の切替も、ほかの製品から移ってきた /handoff も）。
  // 前の知らせの帯（.next）はやめた。再読み込みでまた出さないよう state は消す
  useEffect(() => {
    if (switched === undefined) return;
    toast(SWITCHER_MSG.switched(switched || SWITCHER_MSG.noName));
    nav(loc.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switched]);
  // 登録の直後・退会を取り消した直後の一言は通知（#168。前は本文の上の帯）。同じ key なので2回出しても1つ
  useEffect(() => {
    if (justRegistered) notify({ key: "home-registered", text: "登録が完了しました。" });
  }, [justRegistered]);
  useEffect(() => {
    if (reopened) notify({ key: "home-reopened", text: "退会を取り消しました。今までどおり使えます。データはそのまま残っています。", testId: "reopened" });
  }, [reopened]);
  const home = useQuery({ queryKey: ["home"], queryFn: () => api<HomeSummary>("GET", "/home") });
  const rep = useReportCountState(me.features.schedule);
  const reports = rep.count;
  const ann = useAnnouncements();
  /** お知らせの帯・報告のカード・本日のカードは、3つとも届いてから一緒に描く（#179：後から入った帯・カードに下のカードの「表示」が押し下げられる） */
  const settled = !home.isPending && !ann.isPending && !rep.loading;
  const h = home.data;
  /** {deliveryNote}（#266）：sales の会社で使う機能「{deliveryNote}」がオン（API は sales の会社に数を返す） */
  const dn = h?.deliveryNotes ?? { undelivered: 0, restAmount: 0, deliveredUnbilled: 0 };
  const dnOn = !!h?.deliveryNotes && uses(me, "deliveryNote");
  /** 本日の業務のカードの枚数（下の並びと同じ条件） */
  const cardCount = !h
    ? 0
    : 3 +
      (dnOn ? 1 : 0) +
      (uses(me, "vendorQuote") && (h.vendorQuotes.review || h.vendorQuotes.failed || h.vendorQuotes.reading) ? 1 : 0) +
      (uses(me, "rfq") && h.rfqWaiting.count ? 1 : 0) +
      (h.mailWatch && h.mailWatch.bounced + h.mailWatch.unopened > 0 ? 1 : 0) +
      (reports ? 1 : 0);
  /** 見出しの「？」（#92）：その時に出ているカードの使い方の説明をここにまとめる（1画面の「？」は3つまで） */
  const todayHelp = h
    ? [
        h.hasAny ? HOME_MSG.helpDecide : HOME_MSG.helpFirst,
        h.hasAny && justRegistered && me.tenant.needsCompanyName ? HOME_MSG.helpCompanyName : "",
      ].join("")
    : null;
  return (
    <>
      <div className="hrow center hhead">
        <h1>
          {HOME_MSG.title}
          {todayHelp ? <Help testId="help-today" label={HOME_MSG.helpLabel} text={todayHelp} /> : null}
        </h1>
        <span className="pill hdate" data-testid="home-today">
          <Icon k="cal" />
          {fmtToday()}
        </span>
        <span className="sp" />
        {h?.hasAny ? (
          <Link className="btn primary hb" to="/estimates/new" title={HOME_MSG.create} data-testid="home-create">
            <Icon k="plus" />
            <BtnText text={HOME_MSG.create} />
          </Link>
        ) : null}
      </div>
      {/* 運営からのお知らせ（#124 の 3a）：閉じたら出ない */}
      {settled ? <AnnouncementBanner /> : null}
      {home.isError ? <ErrorText>{messageOf(home.error)}</ErrorText> : null}
      {!settled ? (
        <p className="hint">{HOME_MSG.loading}</p>
      ) : !h || !h.hasAny ? (
        <>
          {/* {contractor}からの報告（#103）は見積が0枚の会社にも出す（工程表だけ使う会社） */}
          {reports ? (
            <div className="cards hc-solo">
              <Card title={PARTNER_LINK_MSG.homeCard} icon="clock" n={reports} sub={null} testId="home-reports" onOpen={() => nav("/schedule")} />
            </div>
          ) : null}
          <div className="empty">
            <div>
              <p style={{ fontSize: "var(--fs-22)", fontWeight: 700, margin: "0 0 8px", lineHeight: 1.5 }}>{HOME_MSG.firstTitle}</p>
              <p style={{ margin: "0 0 20px", lineHeight: 1.5 }}>{t(HOME_MSG.firstBody)}</p>
              <Link className="btn primary big hb" to="/estimates/new" title={HOME_MSG.create}>
                <BtnText text={`${HOME_MSG.create} ›`} />
              </Link>
            </div>
            <div className="sheet">見積書</div>
          </div>
          <div className="cards later">
            {(
              [
                ["pen", HOME_MSG.drafts],
                ["clock", HOME_MSG.waiting],
                ["inv", HOME_MSG.toBill],
              ] as const
            ).map(([k, title]) => (
              <section className="card zero" key={title}>
                <CardTitle icon={k} title={title} />
                <div className="hc-row">
                  <span className="hc-num zero">
                    0<small>件</small>
                  </span>
                </div>
                <p className="hc-sub">{HOME_MSG.none}</p>
              </section>
            ))}
          </div>
        </>
      ) : (
        <>
          {/* カードは1つの格子（PC は1行に4枚・幅 1200px 未満は2列・スマホ版は1列）。5枚目からは次の行 */}
          {/* 5枚のときは1行に5枚（#266、Figma ③。幅 1200px 以上。6枚からは今までどおり4枚ずつ） */}
          <div className={"cards" + (cardCount === 5 ? " is-five" : "")} data-testid="home-cards">
            <Card
              title={HOME_MSG.drafts}
              icon="pen"
              n={h.drafts.count}
              sub={!h.drafts.count ? HOME_MSG.none : h.drafts.oldestIssueDate ? HOME_MSG.oldest(daysSince(h.drafts.oldestIssueDate), md(h.drafts.oldestIssueDate)) : null}
              onOpen={() => nav("/estimates?f=draft")}
            />
            <Card
              title={HOME_MSG.waiting}
              icon="clock"
              n={h.waiting.count}
              sub={h.waiting.count ? `${HOME_MSG.total} ${yen(h.waiting.total)} 円` : HOME_MSG.none}
              onOpen={() => nav("/estimates?f=waiting")}
            />
            {/* 未納品（#266、docs/96・Figma ③）：受注と未請求のあいだ。使う機能「{deliveryNote}」の会社だけ。残のある受注の数と残の合計（税抜） */}
            {dnOn ? (
              <Card
                title={DELIVERY_NOTE_MSG.homeCard}
                icon="truck"
                n={dn.undelivered}
                sub={dn.undelivered ? DELIVERY_NOTE_MSG.homeRest.replace("{amount}", yen(dn.restAmount)) : HOME_MSG.none}
                testId="home-undelivered"
                onOpen={() => nav("/estimates?f=ordered")}
              />
            ) : null}
            <Card
              title={HOME_MSG.toBill}
              icon="inv"
              // 締め（#267）：締め日が来た{customer}があれば、その数も数えて締めの画面へ
              // 定期請求（#268）：定期請求の下書きがあれば「定期 n件（下書き）」も数えて定期請求の画面へ
              n={h.toBill.count || (h.closingDue?.count ?? 0) || (h.recurringDrafts?.count ?? 0)}
              sub={(() => {
                const rest =
                  dnOn && dn.deliveredUnbilled
                    ? t(DELIVERY_NOTE_MSG.homeDelivered).replace("{n}", String(dn.deliveredUnbilled))
                    : h.toBill.count
                      ? `${HOME_MSG.total} ${yen(h.toBill.total)} 円${h.toBill.drafts ? `　${HOME_MSG.toBillDrafts(h.toBill.drafts)}` : ""}`
                      : null;
                const due = h.closingDue ? CLOSING_MSG.homeDue(h.closingDue.count, Number(h.closingDue.month.slice(5, 7))) : null;
                const rec = h.recurringDrafts ? RECURRING_MSG.homeDrafts(h.recurringDrafts.count) : null;
                const head = [due, rec].filter(Boolean).join("　");
                return head ? `${head}${rest ? `　${rest}` : ""}` : (rest ?? HOME_MSG.none);
              })()}
              testId="home-to-bill"
              onOpen={() => nav(h.closingDue ? `/closing?month=${h.closingDue.month}` : h.recurringDrafts ? `/recurring?month=${h.recurringDrafts.month}` : dnOn && dn.deliveredUnbilled ? "/delivery-notes?tab=issued" : "/estimates?f=ordered")}
            />
            {/* 確認待ちの{vendorQuote}（#171：前は「今日やること」の上の帯）。使う機能「{vendorQuote}の取込」の会社だけ（#142 段3） */}
            {uses(me, "vendorQuote") && (h.vendorQuotes.review || h.vendorQuotes.failed || h.vendorQuotes.reading) ? <VendorQuoteCard vq={h.vendorQuotes} /> : null}
            {/* {vendor}の未回答（#119、docs/56 §6.6）。使う機能 rfq（見積依頼。#149）の会社で、1件以上のときだけ */}
            {uses(me, "rfq") && h.rfqWaiting.count ? (
              <Card
                title={t(RFQ_MSG.cardTitle)}
                icon="clock"
                n={h.rfqWaiting.count}
                sub={
                  h.rfqWaiting.overdue
                    ? { node: <b className="shu" data-testid="home-rfq-overdue">{RFQ_MSG.cardOverdue.replace("{n}", String(h.rfqWaiting.overdue))}</b>, text: RFQ_MSG.cardOverdue.replace("{n}", String(h.rfqWaiting.overdue)) }
                    : h.rfqWaiting.nextReplyBy
                      ? RFQ_MSG.cardNext.replace("{d}", md(h.rfqWaiting.nextReplyBy))
                      : null /* 回答期限の無い依頼だけ（1件以上なので「対象なし」は出さない） */
                }
                onOpen={() => nav("/rfqs")}
              />
            ) : null}
            {/* メールの確認（#225 ★4、docs/91 §3.7）：届いていません・未取得 7日（確認済みは除く）。1件以上のときだけ。押すと「送付状」の一覧の「要確認」 */}
            {h.mailWatch && h.mailWatch.bounced + h.mailWatch.unopened > 0 ? <MailWatchCard mw={h.mailWatch} onOpen={() => nav("/cover-letters?f=attention")} /> : null}
            {/* 協力会社からの報告（#103 §6.3.6）：確かめ待ちがあるときだけ。押すとメニュー「工程表」へ */}
            {reports ? <Card title={PARTNER_LINK_MSG.homeCard} icon="clock" n={reports} sub={null} testId="home-reports" onOpen={() => nav("/schedule")} /> : null}
          </div>
          {/* 部品の差し込み口（入金待ち。sales の会社だけ＝営業力と、2026-09-29 からは現場力も） */}
          <Slot name="home.cards" me={me} home={h} />
          {/* 支払予定（#233 3a、docs/93 §15.10 ②）：入金待ちと同じ作りで、その下。sales の会社だけ（API が返したときだけ） */}
          <HomePurchasesDue home={h} />
          <MonthBand months={h.months} />
        </>
      )}
      {toastNode}
    </>
  );
}

/** 副題：文字だけか、色を付けた中身（node）と title に入れる文（text） */
type Sub = string | { node: React.ReactNode; text: string } | null;
const subText = (s: Sub) => (s == null ? undefined : typeof s === "string" ? s : s.text);
/** 副題の無いカード（報告）も1行ぶんの高さを取る（ほかのカードと高さをそろえる） */
const subNode = (s: Sub) => (s == null ? "\u00a0" : typeof s === "string" ? s : s.node);

/** カードの題の行：丸い印 28px（淡い地・藍の線 16px）＋題 15px 太字（長い題は1行で「…」・全文は title） */
function CardTitle({ icon, title }: { icon: "pen" | "clock" | "inv" | "doc" | "send" | "truck"; title: string }) {
  return (
    <h3 className="hc-title" title={title}>
      <span className="hc-mark" aria-hidden="true">
        <Icon k={icon} />
      </span>
      <span className="hc-tt">{title}</span>
    </h3>
  );
}

/** 「表示 →」（#226）：1件以上は藍の塗り（右に矢印）・0件は枠のボタン（矢印なし）。高さ 36px・幅 96px（スマホ版は押す所 56px） */
function ShowButton({ n, title, onOpen, testId }: { n: number; title: string; onOpen: () => void; testId?: string }) {
  return (
    <button type="button" className={n ? "btn primary hc-go" : "btn hc-go"} onClick={onOpen} title={HOME_MSG.showTitle(title)} data-testid={testId}>
      {HOME_MSG.show}
      {n ? <Icon k="arrow" /> : null}
    </button>
  );
}

/**
 * カード（9/29 → 10/21 #226）：題の行 → 数の行（件数 32px・0 は薄墨／右端に「表示」）→ 副題 1行（「…」・全文は title）。
 * 高さは内容なり（約 122px）。枠線なし・影だけ
 */
function Card({ title, icon, n, sub, onOpen, testId, shu }: { title: string; icon: "pen" | "clock" | "inv" | "doc" | "send" | "truck"; n: number; sub: Sub; onOpen: () => void; testId?: string; shu?: boolean }) {
  return (
    <section className={"card" + (n ? "" : " zero")} data-testid={testId}>
      <CardTitle icon={icon} title={title} />
      <div className="hc-row">
        <span className={"hc-num" + (n ? "" : " zero") + (shu ? " shu" : "")} data-testid={`card-${title}`}>
          {n}
          <small>件</small>
        </span>
        <span className="sp" />
        <ShowButton n={n} title={title} onOpen={onOpen} />
      </div>
      <p className="hc-sub" title={subText(sub)}>
        {subNode(sub)}
      </p>
    </section>
  );
}

/** メールの確認（#225）：届いていません（朱）・未取得 7日 を副題の1行に */
function MailWatchCard({ mw, onOpen }: { mw: NonNullable<HomeSummary["mailWatch"]>; onOpen: () => void }) {
  const bounced = mw.bounced ? MAIL_MSG.homeBounced.replace("{n}", String(mw.bounced)) : "";
  const unopened = mw.unopened ? MAIL_MSG.homeUnopened.replace("{d}", String(MAIL_LIMITS.unopenedDays)).replace("{n}", String(mw.unopened)) : "";
  return (
    <Card
      title={MAIL_MSG.homeCard}
      icon="send"
      n={mw.bounced + mw.unopened}
      testId="home-mail-watch"
      sub={{
        node: (
          <>
            {bounced ? <b className="shu" data-testid="home-mail-bounced">{bounced}</b> : null}
            {bounced && unopened ? "　" : null}
            {unopened ? <span data-testid="home-mail-unopened">{unopened}</span> : null}
          </>
        ),
        text: [bounced, unopened].filter(Boolean).join("　"),
      }}
      onOpen={onOpen}
    />
  );
}

/**
 * 確認待ちの{vendorQuote}（#171 → #226。前は「AI読み取り済みの{vendorQuote}」と下に1件ずつのリンク）。
 * 数は確認待ち（読み取り済み）の件数。副題は1件目の{project}名＋「ほか n 件」、読み取れなかった分（朱）・AI読み取り中も同じ1行に。
 * 読み取れなかった分だけなら題は「読み取れなかった{vendorQuote}」・数は朱。「表示」は{vendorQuote}の一覧（#198。読み取れなかった分だけなら要確認のタブ）
 */
function VendorQuoteCard({ vq }: { vq: HomeSummary["vendorQuotes"] }) {
  useLabels();
  const nav = useNavigate();
  const failedOnly = !vq.review && !!vq.failed;
  const n = vq.review || vq.failed;
  const title = t(failedOnly ? HOME_MSG.vendorFailed : HOME_MSG.vendorReview);
  const shown = vq.items.filter((x) => x.status === (failedOnly ? "failed" : "review"));
  const first = shown[0];
  const head = first ? HOME_MSG.others(first.groupName || first.vendorName, Math.max(0, n - 1)) : "";
  const failed = vq.review && vq.failed ? t(HOME_MSG.vendorFailedN(vq.failed)) : "";
  const reading = vq.reading ? HOME_MSG.vendorReadingN(vq.reading) : "";
  const text = [head, failed, reading].filter(Boolean).join("　") || HOME_MSG.none;
  return (
    <section className={"card vqcard" + (n ? "" : " zero")} data-testid="home-vendor-quotes">
      <CardTitle icon="doc" title={title} />
      <div className="hc-row">
        <span className={"hc-num" + (n ? "" : " zero") + (failedOnly ? " shu" : "")} data-testid="home-vq-count">
          {n}
          <small>件</small>
        </span>
        <span className="sp" />
        <ShowButton n={n} title={title} onOpen={() => nav(failedOnly ? "/vendor-quotes?state=review" : "/vendor-quotes")} testId="home-vq-list" />
      </div>
      <p className="hc-sub" title={text} data-testid="home-vq-sub">
        {head ? <span>{head}</span> : null}
        {head && failed ? "　" : null}
        {failed ? <b className="shu">{failed}</b> : null}
        {(head || failed) && reading ? "　" : null}
        {reading ? <span>{reading}</span> : null}
        {!head && !failed && !reading ? HOME_MSG.none : null}
      </p>
    </section>
  );
}

/**
 * 今月の実績（#52 → 10/21 #226。前は「今月の数字」のカード5枚）：カードを並べず1行の帯（高さ 約64px）。
 * 「今月の実績 10月｜見積発行 n 件 / 金額｜受注 n 件 / 金額｜失注 n 件｜利益 金額（利益率）｜未入金 n 件 / 金額」。今月／先月は右端の選ぶ欄。
 * 出した見積・受注・失注は税込、利益だけ税抜（説明は「？」）。利益は受注した見積の仕入れが全部入っているときだけ数字を出す（ルール7）。
 * 今月も先月も 0件 なら帯ごと出さない（空の数字を並べない）。請求・入金の額は API に無いので出さない（#226 で API は足さない）
 */
function MonthBand({ months }: { months: HomeSummary["months"] }) {
  const [sel, setSel] = useState<"this" | "last">("this");
  const any = [months.thisMonth, months.lastMonth].some((x) => x.issued.count + x.ordered.count + x.lost.count > 0) || !!months.thisMonth.unpaid?.count || !!months.thisMonth.payable?.count || !!months.thisMonth.paid?.count || !!months.lastMonth.paid?.count;
  if (!any) return null;
  const m = sel === "this" ? months.thisMonth : months.lastMonth;
  const unpaid = sel === "this" ? m.unpaid : undefined;
  const payable = sel === "this" ? m.payable : undefined;
  // 支払（#233 3c）：その月の支払の額の合計（今月・先月とも。sales の会社だけ）
  const paid = m.paid;
  return (
    <section className="mband" data-testid="month-figures">
      <h2 className="mb-title">
        {sel === "this" ? HOME_MSG.month : HOME_MSG.lastMonth}
        <span className="mb-month num">{+m.month.slice(5, 7)}月</span>
        <Help testId="help-month" label={HOME_MSG.helpMonthLabel} text={HOME_MSG.helpMonth + (unpaid ? HOME_MSG.helpMonthUnpaid : "") + (payable ? PURCHASE_MSG.helpMonthPayable : "") + (paid ? PURCHASE_MSG.helpMonthPaid : "")} />
      </h2>
      <div className="mb-items">
        <MonthItem label={HOME_MSG.issued} f={m.issued} />
        <MonthItem label={HOME_MSG.ordered} f={m.ordered} />
        <MonthItem label={HOME_MSG.lost} f={m.lost} />
        <span className="mb-item" data-testid={`tile-${HOME_MSG.profit}`}>
          <span className="mb-l">{HOME_MSG.profit}</span>{" "}
          {m.profit.missing > 0 ? (
            <b className="shu mb-miss">{HOME_MSG.costMissing(m.profit.missing)}</b>
          ) : m.profit.amount != null ? (
            <>
              <b className="mb-v ai num">{yen(m.profit.amount)}</b> <span className="mb-u">円</span>
              {m.profit.rate != null ? <span className="mb-rate num">{HOME_MSG.profitRate(m.profit.rate)}</span> : null}
            </>
          ) : (
            <span className="mb-none">—</span>
          )}
        </span>
        {/* 未入金（E1。sales の会社の今月だけ）：いまの残額の合計と、そのうち期限超過（色だけでなく文字で） */}
        {unpaid ? (
          <span className="mb-item" data-testid={`tile-${HOME_MSG.unpaid}`}>
            <span className="mb-l">{HOME_MSG.unpaid}</span> <b className={"mb-v num" + (unpaid.count ? "" : " zero")}>{unpaid.count}</b> <span className="mb-u">件</span>
            {unpaid.count ? (
              <>
                {" "}
                <span className="mb-u">/</span> <b className="mb-v num">{yen(unpaid.total)}</b> <span className="mb-u">円</span>
              </>
            ) : null}
            {unpaid.overdueTotal ? (
              <span className="shu mb-over">
                {HOME_MSG.overdueSum} <span className="num">{yen(unpaid.overdueTotal)}</span> 円
              </span>
            ) : null}
          </span>
        ) : null}
        {/* 未払い（#233 3a。sales の会社の今月だけ）：確定した仕入の残の合計と、そのうち期限超過 */}
        {payable ? (
          <span className="mb-item" data-testid={`tile-${PURCHASE_MSG.monthPayable}`}>
            <span className="mb-l">{PURCHASE_MSG.monthPayable}</span> <b className={"mb-v num" + (payable.count ? "" : " zero")}>{payable.count}</b> <span className="mb-u">件</span>
            {payable.count ? (
              <>
                {" "}
                <span className="mb-u">/</span> <b className="mb-v num">{yen(payable.total)}</b> <span className="mb-u">円</span>
              </>
            ) : null}
            {payable.overdueTotal ? (
              <span className="shu mb-over">
                {HOME_MSG.overdueSum} <span className="num">{yen(payable.overdueTotal)}</span> 円
              </span>
            ) : null}
          </span>
        ) : null}
        {paid ? <MonthItem label={PURCHASE_MSG.monthPaid} f={paid} /> : null}
      </div>
      <select className="mb-pick" aria-label={HOME_MSG.monthPick} value={sel} onChange={(e) => setSel(e.target.value as "this" | "last")}>
        <option value="this">{HOME_MSG.monthThis}</option>
        <option value="last">{HOME_MSG.monthLast}</option>
      </select>
    </section>
  );
}

/** 帯の1項目：名前 14px 薄墨＋件数 20px 太字＋「件」、件数があれば「/ 金額 円」 */
function MonthItem({ label, f }: { label: string; f: MonthFigures["issued"] }) {
  return (
    <span className="mb-item" data-testid={`tile-${label}`}>
      <span className="mb-l">{label}</span> <b className={"mb-v num" + (f.count ? "" : " zero")}>{f.count}</b> <span className="mb-u">件</span>
      {f.count ? (
        <>
          {" "}
          <span className="mb-u">/</span> <b className="mb-v num">{yen(f.total)}</b> <span className="mb-u">円</span>
        </>
      ) : null}
    </span>
  );
}

/**
 * ホームのボタンの名前（#104 §7.1 の2）：2行まで折り返し、それでも入らなければ「…」。全部の名前はボタンの title。
 * 押せる所はスマホで 56px（styles.css の .hb）
 */
function BtnText({ text }: { text: string }) {
  return <span className="bl">{text}</span>;
}
